// ─── helpKnowledge.js ────────────────────────────────────────────────────────
// The curated knowledge map behind the Help flyout: every dashboard surface,
// what it does, how to do the common things, and where it lives. Entries are
// matched by keyword scoring in HelpFlyout; each answer deep-links to its
// page. This same map is the grounding a future LLM-backed guide would use —
// keep it accurate and it serves both.

export const HELP_TOPICS = [
  {
    route: "/", page: "Overview",
    title: "The ASK ME operator",
    body: "This drawer answers questions about any part of the dashboard and can take actions for you \u2014 run an agent, mark a lead contacted, assign a lead, pause an agent, dismiss a finding. It always shows you exactly what it will do and waits for your Confirm; nothing happens without it. Your role's permissions apply as usual.",
    keywords: ["ask me", "operator", "assistant", "help", "actions", "confirm", "do it for me"],
  },
  {
    route: "/", page: "Overview",
    title: "The Overview dashboard",
    body: "Your at-a-glance home: recent conversations, lead counts, and headline analytics. Everything here is a summary — click through to the dedicated pages for detail.",
    keywords: ["overview", "home", "dashboard", "summary", "start"],
  },
  {
    route: "/conversations", page: "Conversations",
    title: "Reading & managing chat conversations",
    body: "Every visitor chat lives here: open one to read the full transcript, add internal notes, assign it to a teammate, or delete test conversations. Filters cover status, assignee, and priority.",
    keywords: ["conversations", "chats", "transcripts", "messages", "notes", "assign", "delete conversation", "history"],
  },
  {
    route: "/leads", page: "Leads",
    title: "Working the leads queue",
    body: "Contact-info submissions from the chat widget land here. Change a lead's status (new → contacted → closed), open its originating conversation for context, or delete junk. The 'contacted' status stamps the time automatically.",
    keywords: ["leads", "contacts", "lead status", "follow up", "contacted", "sales"],
  },
  {
    route: "/documents", page: "Documents",
    title: "Uploading documents the assistant can cite",
    body: "Upload PDFs (manuals, guides, spec sheets) and they're indexed into the assistant's knowledge: it answers from them and names the guide. Use the Workstation to read a PDF side-by-side with Q&A. If the assistant can't answer something, the missing manual is usually the reason — the Content Agent's 'true content holes' list says which.",
    keywords: ["documents", "upload", "pdf", "manuals", "knowledge", "indexing", "workstation", "guides", "assistant knowledge"],
  },
  {
    route: "/products", page: "Products",
    title: "Managing the product catalog",
    body: "The products the assistant recommends and the search runs on. Add or edit products, then run 'Generate embeddings' so new items become searchable semantically. CSV import handles bulk loads.",
    keywords: ["products", "catalog", "embeddings", "csv import", "add product", "edit product", "sku"],
  },
  {
    route: "/locations", page: "Locations",
    title: "Dealer maps & locations",
    body: "Each map (dealer map, alignment, mobile tech) has its own locations and toolbar buttons. When adding a location, use '📍 Get coordinates from address' to fill latitude/longitude automatically — a location without coordinates shows in lists but gets no map pin. Toolbar filter buttons are driven by each location's type.",
    keywords: ["locations", "map", "dealers", "pins", "coordinates", "geocode", "address", "latitude", "longitude", "jeep", "rv dealer", "add location", "save location"],
  },
  {
    route: "/agents", page: "Agents",
    title: "The agent console",
    body: "Your autonomous workers: the Location Steward audits the dealer maps (dead websites, missing pins, duplicates), the Insight Agent writes the Monday brief, and the Content Agent drafts answers for questions the assistant couldn't handle. Pause any agent with its toggle, trigger it with Run now, review the steward's findings in the Findings tab, and read briefs in Weekly briefs. Dismiss a finding to mark it reviewed; fix the data and it resolves itself.",
    keywords: ["agents", "agent", "steward", "insight", "content agent", "findings", "brief", "weekly brief", "run now", "pause agent", "automation", "autonomous", "dismiss finding"],
  },
  {
    route: "/analytics", page: "Analytics",
    title: "Chat & marketing analytics",
    body: "Conversation volume, top intents, sentiment, the customer funnel, plus GA4 traffic and Search Console data once those integrations are connected (Settings). Date-range controls sit at the top of each panel.",
    keywords: ["analytics", "metrics", "stats", "sentiment", "intents", "funnel", "ga4", "google analytics", "search console", "traffic"],
  },
  {
    route: "/reports", page: "Reports",
    title: "Generating shareable reports",
    body: "Pick the metric groups you want, preview, and generate an AI-drafted report you can edit and export with your branding — built for sending to stakeholders.",
    keywords: ["reports", "export", "pdf report", "stakeholders", "generate report"],
  },
  {
    route: "/widget", page: "Chat Widget",
    title: "Configuring the chat widget",
    body: "Everything visitors see: colors, greeting, suggested questions, product cards — and the behavior knobs: the retrieval threshold (lower = more products matched; 0.35 is the tuned default), lead capture settings, and the consent mode. Consent 'explicit' requires visitors to accept your Terms of Service before chatting; 'notice' shows a disclosure line; the consent text and Terms/Privacy URLs are editable here too.",
    keywords: ["widget", "chat widget", "consent", "terms of service", "tos", "privacy policy", "greeting", "colors", "threshold", "retrieval", "lead capture", "accept terms", "embed", "install widget"],
  },
  {
    route: "/training", page: "Training",
    title: "Teaching the assistant",
    body: "Three tools: Q&A pairs (exact answers for exact questions), the corrections queue (thumbs-down answers and Content Agent drafts awaiting your review — approve or edit them and the assistant learns), and guardrails (hard rules it must follow). Drafts marked 'pending' do nothing until you approve them.",
    keywords: ["training", "teach", "corrections", "qa pairs", "q&a", "guardrails", "review queue", "approve draft", "content agent drafts", "wrong answer", "thumbs down"],
  },
  {
    route: "/document-widget", page: "Doc Widget",
    title: "The document search widget",
    body: "A separate embeddable widget that lets visitors search and read your indexed documents directly — configuration and embed code live here.",
    keywords: ["doc widget", "document widget", "document search", "embed documents"],
  },
  {
    route: "/floor", page: "Floor Access",
    title: "Floor access PIN",
    body: "The PIN-protected kiosk view for on-site use. Change the PIN here — and never leave it as a default.",
    keywords: ["floor", "pin", "kiosk", "floor access"],
  },
  {
    route: "/api-docs", page: "API & MCP",
    title: "API & MCP access",
    body: "Endpoints and keys for integrating the platform's search and chat into your own systems, plus MCP connection details.",
    keywords: ["api", "mcp", "integration", "endpoints", "api keys", "developers"],
  },
  {
    route: "/team", page: "Team",
    title: "Team members & roles",
    body: "Invite teammates and set roles: owners manage everything, admins manage content and settings, editors change content, viewers read, sales works conversations and leads. Role decides what each person sees and can do across the dashboard.",
    keywords: ["team", "users", "invite", "roles", "permissions", "owner", "admin", "editor", "viewer", "sales", "add user"],
  },
  {
    route: "/billing", page: "Billing",
    title: "Plan & billing",
    body: "Your plan, monthly conversation usage against its limit, and the Stripe billing portal. Conversation counts reset on the 1st of each month.",
    keywords: ["billing", "plan", "subscription", "payment", "invoice", "conversation limit", "usage"],
  },
  {
    route: "/settings", page: "Settings",
    title: "Account settings & integrations",
    body: "Organization context the assistant uses, AI provider/model selection, theme and branding, and the Google Analytics / Search Console integrations that feed the marketing analytics.",
    keywords: ["settings", "integrations", "theme", "branding", "ai model", "provider", "organization", "connect google"],
  },
];
